import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';
import type { AdminUser, RoleId } from '@vedjegy/shared';
import { ROLE_LABELS } from '@vedjegy/shared';

const ALL_ROLES: RoleId[] = ['company_user', 'reviewer', 'tenant_admin', 'super_admin'];

const ROLE_COLORS: Record<RoleId, string> = {
  company_user: 'var(--muted-foreground)',
  reviewer:     '#0077cc',
  tenant_admin: 'var(--secondary-foreground)',
  super_admin:  'var(--destructive)',
};

export default function AdminUsersPage() {
  const [users, setUsers]           = useState<AdminUser[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [editingId, setEditingId]   = useState<string | null>(null);
  const [editRoles, setEditRoles]   = useState<RoleId[]>([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState('');
  const [deactivating, setDeactivating] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.admin.listUsers();
      setUsers(res.users);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Hiba a betöltés során.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function startEdit(user: AdminUser) {
    setEditingId(user.id);
    setEditRoles([...user.roles]);
    setEmailDraft(user.email);
    setSaveError('');
  }

  function cancelEdit() {
    setEditingId(null);
    setSaveError('');
  }

  function toggleRole(role: RoleId) {
    setEditRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  async function deactivateUser(user: AdminUser) {
    if (!window.confirm(`Biztosan letiltja ezt a felhasználót?\n\n${user.email}\n\nA művelet nem visszavonható — az összes szerepköre törlődik.`)) return;
    setDeactivating(user.id);
    try {
      await api.admin.deactivateUser(user.id);
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, isActive: false, roles: [] } : u));
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Hiba a letiltás során.');
    } finally {
      setDeactivating(null);
    }
  }

  async function saveChanges(user: AdminUser) {
    const normalizedEmail = emailDraft.trim().toLowerCase();
    const emailChanged = normalizedEmail !== user.email;

    if (emailChanged) {
      const ok = window.confirm(
        `Biztosan megváltoztatja az e-mail címet?\n\nRégi: ${user.email}\nÚj:   ${normalizedEmail}`,
      );
      if (!ok) return;
    }

    setSaving(true);
    setSaveError('');
    try {
      if (emailChanged) {
        await api.admin.updateUserEmail(user.id, normalizedEmail);
      }
      await api.admin.updateUserRoles(user.id, editRoles);
      setUsers((prev) => prev.map((u) =>
        u.id === user.id ? { ...u, email: emailChanged ? normalizedEmail : u.email, roles: editRoles } : u,
      ));
      setEditingId(null);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Mentési hiba.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout>
      <div style={{ padding: '32px 40px' }}>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 6 }}>
            ADMINISZTRÁCIÓ
          </div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--primary)' }}>Felhasználók</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--muted-foreground)' }}>
            Szerepkörök kezelése a tenant összes felhasználójánál.
          </p>
        </div>

        {loading && <div style={{ color: 'var(--muted-foreground)', fontSize: 14 }}>Betöltés…</div>}
        {error && (
          <div className="alert alert-destructive" style={{ marginBottom: 20 }}>
            <span>⚠</span><div><p>{error}</p></div>
          </div>
        )}

        {!loading && !error && (
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--muted)' }}>
                  {['E-mail', 'Vállalkozás', 'Szerepkörök', 'Utolsó belépés', ''].map((h) => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted-foreground)' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid var(--border)' }}>

                    {/* Email cell */}
                    <td style={{ padding: '14px 16px', fontSize: 14, color: 'var(--primary)', fontWeight: 600 }}>
                      {user.email}
                      {!user.isActive && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--destructive)', fontWeight: 700 }}>LETILTVA</span>}
                    </td>

                    {/* Company */}
                    <td style={{ padding: '14px 16px', fontSize: 13, color: 'var(--muted-foreground)' }}>
                      {user.companyName ?? '—'}
                    </td>

                    {/* Roles cell — edit panel spans here */}
                    <td style={{ padding: '14px 16px' }}>
                      {editingId === user.id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {/* Email input */}
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.06em' }}>E-mail cím</div>
                            <input
                              type="email"
                              value={emailDraft}
                              onChange={(e) => setEmailDraft(e.target.value)}
                              style={{ fontSize: 13, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', width: 260 }}
                              autoFocus
                            />
                          </div>
                          {/* Role toggles */}
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Szerepkörök</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {ALL_ROLES.map((role) => {
                                const active = editRoles.includes(role);
                                return (
                                  <button
                                    key={role}
                                    onClick={() => toggleRole(role)}
                                    style={{
                                      padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                                      border: `2px solid ${active ? ROLE_COLORS[role] : 'var(--border)'}`,
                                      background: active ? `${ROLE_COLORS[role]}18` : '#fff',
                                      color: active ? ROLE_COLORS[role] : 'var(--muted-foreground)',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {ROLE_LABELS[role]}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          {saveError && <span style={{ fontSize: 12, color: 'var(--destructive)' }}>{saveError}</span>}
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button className="btn btn-primary btn-sm" onClick={() => void saveChanges(user)} disabled={saving}>
                              {saving ? 'Mentés…' : 'Mentés'}
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={cancelEdit}>Mégse</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                          {user.roles.map((role) => (
                            <span
                              key={role}
                              style={{
                                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                                background: `${ROLE_COLORS[role as RoleId] ?? 'var(--muted-foreground)'}18`,
                                color: ROLE_COLORS[role as RoleId] ?? 'var(--muted-foreground)',
                                border: `1px solid ${ROLE_COLORS[role as RoleId] ?? 'var(--border)'}44`,
                              }}
                            >
                              {ROLE_LABELS[role as RoleId] ?? role}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>

                    {/* Last login */}
                    <td style={{ padding: '14px 16px', fontSize: 13, color: 'var(--muted-foreground)' }}>
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleDateString('hu-HU')
                        : '—'}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '14px 16px' }}>
                      {editingId !== user.id && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(user)} disabled={!user.isActive}>
                            Szerkesztés
                          </button>
                          {user.isActive && (
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ color: 'var(--destructive)' }}
                              onClick={() => void deactivateUser(user)}
                              disabled={deactivating === user.id}
                            >
                              {deactivating === user.id ? 'Letiltás…' : 'Letiltás'}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 14 }}>Nincsenek felhasználók.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
